import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { formatDateTime } from "@/lib/dates";
import { Link2, Link2Off, CheckCircle2, XCircle, RefreshCw, History, AlertTriangle, Building2, User, CalendarClock, Activity, UserPlus, FileCheck, PlayCircle, AlertCircle, KeyRound, ShieldCheck, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function XeroSettings() {
  const [status, setStatus] = useState(null);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);
  const [diagBusy, setDiagBusy] = useState("");
  const [diagResult, setDiagResult] = useState(null);
  const [testInv, setTestInv] = useState("INV-0005");
  const [params, setParams] = useSearchParams();
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [editingCreds, setEditingCreds] = useState(false);
  const [savingCreds, setSavingCreds] = useState(false);
  const [preview, setPreview] = useState(null);
  const [scopeCheck, setScopeCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [oauthDbg, setOauthDbg] = useState(null);
  const [dbgLoading, setDbgLoading] = useState(false);
  const [webhookKey, setWebhookKey] = useState("");
  const [savingWebhook, setSavingWebhook] = useState(false);

  const load = () => {
    api.get("/xero/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/xero/audit").then((r) => setAudit(r.data)).catch(() => {});
    api.get("/xero/oauth-preview").then((r) => setPreview(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (params.get("connected")) { toast.success("Connected to Xero successfully"); setParams({}); load(); }
    if (params.get("error")) { toast.error(`Xero connection failed: ${params.get("error")}`); setParams({}); }
    // eslint-disable-next-line
  }, [params]);

  const connect = async () => {
    setBusy(true);
    try {
      // Pre-flight: verify the current credentials produce a valid request before leaving the app.
      const dbg = await api.get("/xero/oauth-debug");
      setOauthDbg(dbg.data);
      if (!dbg.data.reached_login) {
        const reason = dbg.data.xero_response?.error_reason || "error";
        toast.error(`Blocked before Xero: ${reason}. Fix credentials — see OAuth Request Inspector below.`);
        setBusy(false);
        document.querySelector('[data-testid="xero-oauth-request-panel"]')?.scrollIntoView({ behavior: "smooth" });
        return;
      }
      const r = await api.get("/xero/connect");
      window.location.href = r.data.auth_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect from Xero? Invoices already synced stay in Xero, but no further sync will occur until you reconnect.")) return;
    setBusy(true);
    try { await api.post("/xero/disconnect"); toast.success("Disconnected from Xero"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveCreds = async () => {
    if (!clientId.trim() || !clientSecret.trim()) { toast.error("Enter both Client ID and Client Secret."); return; }
    setSavingCreds(true);
    try {
      await api.post("/xero/config", { client_id: clientId.trim(), client_secret: clientSecret.trim() });
      toast.success("Xero credentials saved securely.");
      setClientId(""); setClientSecret(""); setEditingCreds(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingCreds(false); }
  };

  const connected = status?.connected;
  const configured = status?.configured;
  const credsSource = status?.credentials_source;

  const runDiag = async (key, fn) => {
    setDiagBusy(key); setDiagResult(null);
    try {
      const r = await fn();
      setDiagResult({ ok: true, msg: r });
      toast.success(r);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      setDiagResult({ ok: false, msg });
      toast.error(msg);
    } finally { setDiagBusy(""); load(); }
  };
  const testConnection = () => runDiag("test", async () => { const r = await api.get("/xero/test-connection"); return `Connection OK — ${r.data.organisation} (${r.data.currency})`; });
  const createTestCustomer = () => runDiag("customer", async () => { const r = await api.post("/xero/test-customer"); return `Test customer created in Xero: ${r.data.name}`; });
  const createTestInvoice = () => runDiag("invoice", async () => { const r = await api.post("/xero/test-invoice", { invoice_number: testInv }); return `${testInv} synced to Xero (ID ${r.data.xero_invoice_id.slice(0, 8)}…)`; });

  const runScopeCheck = async () => {
    setChecking(true); setScopeCheck(null);
    try {
      const r = await api.get("/xero/scope-check");
      setScopeCheck(r.data);
      if (r.data.rejected_scopes?.length) toast.error(`Xero rejected: ${r.data.rejected_scopes.join(", ")}`);
      else toast.success("All scopes accepted by Xero");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setChecking(false); load(); }
  };

  const viewOAuthRequest = async () => {
    setDbgLoading(true); setOauthDbg(null);
    try {
      const r = await api.get("/xero/oauth-debug");
      setOauthDbg(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDbgLoading(false); }
  };

  const saveWebhookKey = async () => {
    if (!webhookKey.trim()) { toast.error("Enter the Xero webhook signing key."); return; }
    setSavingWebhook(true);
    try {
      await api.post("/xero/webhook-config", { webhook_key: webhookKey.trim() });
      toast.success("Webhook signing key saved.");
      setWebhookKey(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingWebhook(false); }
  };
  const copyWebhookUrl = () => { navigator.clipboard?.writeText(status?.webhook_url || ""); toast.success("Webhook URL copied"); };

  return (
    <div className="space-y-6 animate-fade-up max-w-3xl" data-testid="xero-settings-page">
      <div><h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy">Xero Integration</h1><p className="text-slate-500 mt-1">Connect True Seal to your Xero organisation for two-way invoice & payment sync.</p></div>

      {/* Credentials card */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="xero-credentials-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${configured ? "bg-indigo-50" : "bg-slate-100"}`}>
              <KeyRound className={`w-6 h-6 ${configured ? "text-indigo-600" : "text-slate-400"}`} />
            </div>
            <div>
              <p className="font-heading font-semibold text-navy text-lg">API Credentials</p>
              <p className="text-sm text-slate-500">
                {configured
                  ? <>Configured{credsSource === "ui" ? " (managed in-app)" : credsSource === "env" ? " (from server config)" : ""}{status?.client_id_preview ? ` · Client ID ${status.client_id_preview}` : ""}</>
                  : "Enter your Xero app's Client ID and Client Secret to enable the connection."}
              </p>
            </div>
          </div>
          {configured && !editingCreds && (
            <Button variant="outline" onClick={() => setEditingCreds(true)} data-testid="xero-edit-creds-btn">
              <Pencil className="w-4 h-4 mr-2" />Update Credentials
            </Button>
          )}
        </div>

        {(!configured || editingCreds) && (
          <div className="mt-5 grid grid-cols-1 gap-4" data-testid="xero-creds-form">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Client ID</label>
              <input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="Paste your Xero Client ID"
                className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy" data-testid="xero-client-id-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Client Secret</label>
              <input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="Paste your Xero Client Secret"
                className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy" data-testid="xero-client-secret-input" />
              <p className="text-xs text-slate-400 mt-1">Stored encrypted (AES/Fernet) in the database. The secret is never displayed again.</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={saveCreds} disabled={savingCreds} className="bg-navy hover:bg-navy/90" data-testid="xero-save-creds-btn">
                <ShieldCheck className="w-4 h-4 mr-2" />{savingCreds ? "Saving…" : "Save Credentials"}
              </Button>
              {editingCreds && <Button variant="outline" onClick={() => { setEditingCreds(false); setClientId(""); setClientSecret(""); }} data-testid="xero-cancel-creds-btn">Cancel</Button>}
            </div>
          </div>
        )}
      </div>

      {/* Connection status card */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${connected ? "bg-emerald-50" : "bg-slate-100"}`}>
              {connected ? <CheckCircle2 className="w-6 h-6 text-emerald-600" /> : <XCircle className="w-6 h-6 text-slate-400" />}
            </div>
            <div>
              <p className="font-heading font-semibold text-navy text-lg flex items-center gap-2" data-testid="xero-connection-status">
                {connected ? "Connected" : "Not Connected"}
                {connected && <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid="xero-connected-badge"><CheckCircle2 className="w-3 h-3" />Connected</span>}
              </p>
              <p className="text-sm text-slate-500">{connected ? status?.tenant_name : configured ? "Ready to connect" : "Not configured yet"}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {connected ? (
              <>
                <Button variant="outline" onClick={connect} disabled={busy} data-testid="xero-reconnect-btn">
                  <RefreshCw className="w-4 h-4 mr-2" />Reconnect
                </Button>
                <Button variant="outline" onClick={disconnect} disabled={busy} data-testid="xero-disconnect-btn" className="text-red-600 hover:text-red-700">
                  <Link2Off className="w-4 h-4 mr-2" />Disconnect Xero
                </Button>
              </>
            ) : (
              <Button onClick={connect} disabled={busy || !configured} data-testid="xero-connect-btn" className="bg-[#13B5EA] hover:bg-[#0f9fd0]">
                <Link2 className="w-4 h-4 mr-2" />{busy ? "Redirecting…" : "Connect to Xero"}
              </Button>
            )}
          </div>
        </div>

        {connected && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 text-sm border-t border-slate-100 pt-5">
            <div className="flex items-center gap-3"><Building2 className="w-5 h-5 text-slate-400" /><div><p className="text-slate-400 text-xs uppercase tracking-wider">Organisation</p><p className="font-medium text-navy" data-testid="xero-org-name">{status?.tenant_name}</p></div></div>
            <div className="flex items-center gap-3"><User className="w-5 h-5 text-slate-400" /><div><p className="text-slate-400 text-xs uppercase tracking-wider">Connected By</p><p className="font-medium text-navy">{status?.connected_by || "—"}</p></div></div>
            <div className="flex items-center gap-3"><CalendarClock className="w-5 h-5 text-slate-400" /><div><p className="text-slate-400 text-xs uppercase tracking-wider">Connection Date</p><p className="font-medium text-navy">{status?.connected_at ? formatDateTime(status.connected_at) : "—"}</p></div></div>
            <div className="flex items-center gap-3"><RefreshCw className="w-5 h-5 text-slate-400" /><div><p className="text-slate-400 text-xs uppercase tracking-wider">Last Sync</p><p className="font-medium text-navy">{status?.last_sync_at ? formatDateTime(status.last_sync_at) : "Never"}</p></div></div>
            {status?.last_success && <div className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-500" /><div><p className="text-slate-400 text-xs uppercase tracking-wider">Last Successful Sync</p><p className="font-medium text-navy" data-testid="xero-last-success">{status.last_success.action} · {formatDateTime(status.last_success.at)}</p></div></div>}
            {status?.last_failure && <div className="flex items-center gap-3"><AlertCircle className="w-5 h-5 text-red-500" /><div><p className="text-slate-400 text-xs uppercase tracking-wider">Last Failed Sync</p><p className="font-medium text-red-600" data-testid="xero-last-failure">{formatDateTime(status.last_failure.at)} · {status.last_failure.error?.slice(0, 60)}</p></div></div>}
          </div>
        )}

        {!configured && (
          <div className="mt-5 flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 text-sm" data-testid="xero-not-configured">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Xero credentials aren't set up yet. Enter your Client ID & Secret above to enable the Connect button.</span>
          </div>
        )}
      </div>

      {/* Diagnostics */}
      {connected && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="xero-diagnostics">
          <h2 className="font-heading font-semibold text-navy mb-1 flex items-center gap-2"><Activity className="w-4 h-4" />Connection Testing & Diagnostics</h2>
          <p className="text-sm text-slate-500 mb-4">Verify the integration without leaving the app. Test actions are clearly labelled in Xero.</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={testConnection} disabled={!!diagBusy} data-testid="xero-test-connection">
              <PlayCircle className="w-4 h-4 mr-2" />{diagBusy === "test" ? "Testing…" : "Test Connection"}
            </Button>
            <Button variant="outline" onClick={createTestCustomer} disabled={!!diagBusy} data-testid="xero-test-customer">
              <UserPlus className="w-4 h-4 mr-2" />{diagBusy === "customer" ? "Creating…" : "Create Test Customer"}
            </Button>
            <div className="flex items-center gap-1">
              <input data-testid="xero-test-invoice-number" value={testInv} onChange={(e) => setTestInv(e.target.value)}
                className="w-28 border border-slate-200 rounded-lg px-2 py-2 text-sm outline-none focus:border-navy" />
              <Button variant="outline" onClick={createTestInvoice} disabled={!!diagBusy} data-testid="xero-test-invoice">
                <FileCheck className="w-4 h-4 mr-2" />{diagBusy === "invoice" ? "Syncing…" : "Sync / Create Test Invoice"}
              </Button>
            </div>
          </div>
          {diagResult && (
            <div data-testid="xero-diag-result" className={`mt-4 flex items-start gap-2 rounded-lg px-3 py-2 text-sm ${diagResult.ok ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-red-50 border border-red-200 text-red-700"}`}>
              {diagResult.ok ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
              <span>{diagResult.msg}</span>
            </div>
          )}
        </div>
      )}

      {/* Requested OAuth Scopes diagnostics */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="xero-scopes-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-heading font-semibold text-navy mb-1 flex items-center gap-2"><ShieldCheck className="w-4 h-4" />Requested OAuth Scopes</h2>
            <p className="text-sm text-slate-500 mb-4">These are the exact granular scopes requested during the Connect flow. Run the self-test to verify Xero accepts each one.</p>
          </div>
          <Button variant="outline" onClick={runScopeCheck} disabled={checking || !configured} data-testid="xero-scope-check-btn">
            <PlayCircle className="w-4 h-4 mr-2" />{checking ? "Testing with Xero…" : "Run Scope Self-Test"}
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          <Button variant="outline" onClick={viewOAuthRequest} disabled={dbgLoading || !configured} data-testid="xero-view-oauth-btn">
            <Activity className="w-4 h-4 mr-2" />{dbgLoading ? "Building request…" : "View OAuth Request"}
          </Button>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="xero-scopes-list">
          {(status?.scopes || []).map((s) => {
            const res = scopeCheck?.results?.find((r) => r.scope === s);
            const rejected = res?.invalid_scope;
            const accepted = res?.accepted;
            return (
              <span key={s} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${rejected ? "bg-red-50 border-red-200 text-red-700" : accepted ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-50 border-slate-200 text-navy"}`}>
                {rejected ? <XCircle className="w-3.5 h-3.5 text-red-500" /> : <CheckCircle2 className={`w-3.5 h-3.5 ${accepted ? "text-emerald-500" : "text-slate-400"}`} />}
                {s}{rejected ? " · rejected" : accepted ? " · ok" : ""}
              </span>
            );
          })}
        </div>
        {status?.scope_string && (
          <>
            <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mt-4 mb-1">Scope string</p>
            <code className="block bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-xs break-all text-slate-500" data-testid="xero-scope-string">{status.scope_string}</code>
          </>
        )}
        {preview?.auth_url && (
          <>
            <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mt-4 mb-1">Exact OAuth authorisation URL</p>
            <code className="block bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-xs break-all text-slate-500" data-testid="xero-oauth-url">{preview.auth_url}</code>
          </>
        )}
        {scopeCheck?.rejected_scopes?.length > 0 && (
          <div className="mt-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2.5 text-sm" data-testid="xero-scope-rejected">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Xero rejected these scopes: {scopeCheck.rejected_scopes.join(", ")}</p>
              <p className="mt-1">This is a Xero <b>app configuration</b> issue, not the app. Sign in to the <a href="https://developer.xero.com/app/manage" target="_blank" rel="noreferrer" className="underline font-medium">Xero Developer portal</a>, open your app, and ensure it's a standard <b>Web app / OAuth 2.0</b> app with Accounting access. If the rejected scope is <code>accounting.transactions</code>, the app cannot create invoices until that scope is available — create a new Web app if needed and paste its Client ID/Secret above.</p>
            </div>
          </div>
        )}
        {scopeCheck && scopeCheck.rejected_scopes?.length === 0 && (
          <div className="mt-4 flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2.5 text-sm" data-testid="xero-scope-ok">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            <span>All requested scopes are accepted by Xero. You're ready to Connect.</span>
          </div>
        )}

        {oauthDbg && (
          <div className="mt-5 border-t border-slate-100 pt-5 space-y-3" data-testid="xero-oauth-request-panel">
            <h3 className="font-heading font-semibold text-navy text-sm">OAuth Request Inspector</h3>

            <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 text-blue-900 rounded-lg px-3 py-2.5 text-sm" data-testid="oauth-dbg-verify">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Currently using <span className="uppercase">{oauthDbg.credentials_source}</span> credentials{oauthDbg.creds_saved_by ? ` · saved by ${oauthDbg.creds_saved_by}` : ""}{oauthDbg.creds_saved_at ? ` · ${formatDateTime(oauthDbg.creds_saved_at)}` : ""}.</p>
                <p className="mt-1">Compare the Client ID below <b>character-for-character</b> with the one in your Xero Web App (Developer portal → your app → Configuration). They must match exactly.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 text-sm">
              <div><p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Client ID currently in use (full — compare to Xero app)</p><code className="block bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 text-xs break-all text-navy font-semibold" data-testid="oauth-dbg-clientid-full">{oauthDbg.client_id_full}</code></div>
              <div><p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Client Secret</p><code className="block bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-xs text-slate-600" data-testid="oauth-dbg-secret-len">{oauthDbg.client_secret_length} characters stored (hidden). A Xero secret is typically ~48 characters.</code></div>
              <div><p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Redirect URI</p><code className="block bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-xs break-all text-slate-600" data-testid="oauth-dbg-redirect">{oauthDbg.redirect_uri}</code></div>
              <div><p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Scope string (raw)</p><code className="block bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-xs break-all text-slate-600" data-testid="oauth-dbg-scope">{oauthDbg.scope_string}</code></div>
              <div><p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Scope string (URL-encoded)</p><code className="block bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-xs break-all text-slate-600" data-testid="oauth-dbg-scope-enc">{oauthDbg.scope_encoded}</code></div>
              <div><p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Full authorize URL</p><code className="block bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-xs break-all text-slate-600" data-testid="oauth-dbg-url">{oauthDbg.authorize_url}</code></div>
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Response from Xero</p>
                <div className={`rounded-lg px-3 py-2.5 text-sm border ${oauthDbg.reached_login ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`} data-testid="oauth-dbg-response">
                  {oauthDbg.reached_login ? (
                    <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />HTTP {oauthDbg.xero_response.status} — reached Xero login/consent (request valid). Safe to Connect.</span>
                  ) : (
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold">Xero returned: {oauthDbg.xero_response.error_reason || "error"}{oauthDbg.xero_response.error_code ? ` (code ${oauthDbg.xero_response.error_code})` : ""}</p>
                        {oauthDbg.xero_response.error_reason === "invalid_scope" && (
                          <p className="mt-1">The request sends only the 5 listed scopes (nothing extra). Xero rejects <code>accounting.transactions</code> for Client ID <b>{oauthDbg.client_id_masked}</b>. This means the Client ID above does <b>not</b> belong to a Web App that grants that scope. Copy the Client ID directly from your confirmed Xero Web App (Configuration page), paste it in API Credentials above, Save, and re-run this — do not Connect until this shows green.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Xero Webhooks (real-time auto-sync) */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="xero-webhook-card">
        <div className="flex items-center gap-3 mb-2">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${status?.webhook_configured ? "bg-emerald-50" : "bg-slate-100"}`}>
            <Activity className={`w-5 h-5 ${status?.webhook_configured ? "text-emerald-600" : "text-slate-400"}`} />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-navy">Real-time Auto-Sync (Webhooks)</h2>
            <p className="text-sm text-slate-500">{status?.webhook_configured ? "Configured — paid/voided invoices update automatically, no manual refresh needed." : "Optional. Xero pushes invoice changes here so payments update automatically."}</p>
          </div>
          {status?.webhook_configured && <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full"><CheckCircle2 className="w-3.5 h-3.5" />Active</span>}
        </div>
        <div className="mt-3">
          <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Delivery URL (paste into Xero → your app → Webhooks)</label>
          <div className="flex gap-2 mt-1">
            <code className="flex-1 bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5 text-xs break-all text-slate-600" data-testid="xero-webhook-url">{status?.webhook_url || "—"}</code>
            <Button size="sm" variant="outline" onClick={copyWebhookUrl} data-testid="xero-webhook-copy">Copy</Button>
          </div>
        </div>
        <div className="mt-3">
          <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Webhook Signing Key</label>
          <div className="flex gap-2 mt-1">
            <input type="password" value={webhookKey} onChange={(e) => setWebhookKey(e.target.value)} placeholder={status?.webhook_configured ? "•••••••• (saved — enter to replace)" : "Paste the key from Xero's Webhooks page"}
              className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy" data-testid="xero-webhook-key-input" />
            <Button onClick={saveWebhookKey} disabled={savingWebhook} className="bg-navy hover:bg-navy/90" data-testid="xero-webhook-save">{savingWebhook ? "Saving…" : "Save Key"}</Button>
          </div>
          <p className="text-xs text-slate-400 mt-1.5">In Xero: open your app → <b>Webhooks</b> → subscribe to <b>Invoices</b> (and Contacts), paste the Delivery URL above, then copy the generated <b>signing key</b> here and Save. Xero will send a validation request which this app answers automatically.</p>
        </div>
      </div>

      {/* Setup guide */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <h2 className="font-heading font-semibold text-navy mb-3">Administrator Setup Guide</h2>
        <ol className="space-y-2.5 text-sm text-slate-600 list-decimal ml-5">
          <li>Sign in to (or create) your <a href="https://www.xero.com" target="_blank" rel="noreferrer" className="text-[#13B5EA] font-medium">Xero organisation</a>.</li>
          <li>Go to the <a href="https://developer.xero.com/app/manage" target="_blank" rel="noreferrer" className="text-[#13B5EA] font-medium">Xero Developer portal</a> → <b>New app</b>. Choose <b>Web app</b> (OAuth 2.0).</li>
          <li>Set the <b>Company/App URL</b> to your app URL, and add this exact <b>OAuth 2.0 Redirect URI</b>:
            <code className="block bg-slate-50 border border-slate-200 rounded-md px-2 py-1 mt-1 text-xs break-all text-navy" data-testid="xero-redirect-uri">{status?.redirect_uri || "…"}</code>
          </li>
          <li>Open your app → <b>Configuration</b> → copy the <b>Client ID</b> and <b>generate a Client Secret</b>.</li>
          <li>Paste the <b>Client ID</b> and <b>Client Secret</b> into the <b>API Credentials</b> section at the top of this page and click <b>Save Credentials</b>. This activates the <b>Connect to Xero</b> button.</li>
          <li>Click <b>Connect to Xero</b>, log in, and <b>authorise</b> your organisation.</li>
          <li>Verify the connection shows <b>Connected</b> with your organisation name above.</li>
        </ol>
        <p className="text-xs text-slate-400 mt-3">Required scopes are requested automatically (granular, current Xero standard): accounting.invoices, accounting.payments, accounting.contacts, accounting.settings.read, accounting.attachments, offline_access.</p>
      </div>

      {/* Audit history */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <h2 className="font-heading font-semibold text-navy mb-3 flex items-center gap-2"><History className="w-4 h-4" />Xero Audit History</h2>
        {audit.length === 0 ? <p className="text-sm text-slate-400">No Xero actions recorded yet.</p> : (
          <div className="space-y-2" data-testid="xero-audit-list">
            {audit.map((a) => (
              <div key={a.id} className="text-xs border-l-2 border-slate-200 pl-3 py-0.5">
                <p className="font-medium text-navy">{a.action}{a.error ? <span className="text-red-600"> — error</span> : ""}</p>
                <p className="text-slate-400">{a.admin} · {formatDateTime(a.at)}{a.detail ? ` · ${a.detail}` : ""}{a.error ? ` · ${a.error}` : ""}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
